import { prettifyAlertValue } from '../models/alert.constants'
import type { AlertDimension, TriggeredAlertOccurrenceDto } from '../models/history.types'
import type { AlertMetricCatalog } from '../models/reports-bridge'

export interface AlertDimensionMapperDependencies {
  formatDate: (value: string) => string
  metricLabel: AlertMetricCatalog['metricLabel']
}

function dimensionValue(
  value: unknown,
  formatDate: AlertDimensionMapperDependencies['formatDate'],
): string | null {
  if (typeof value !== 'string' || !value || value.trim() === '__all__') return null
  if (/^\d{4}-\d{2}-\d{2}[tT ]/u.test(value)) return formatDate(value)
  return prettifyAlertValue(value)
}

/** Port of getDimensionInfo; returns the domain's empty array instead of a nullable list. */
export function alertDimensions(
  occurrence: TriggeredAlertOccurrenceDto,
  dependencies: AlertDimensionMapperDependencies,
): AlertDimension[] {
  const result: AlertDimension[] = []
  const add = (field: string, rawValue: unknown): void => {
    const value = dimensionValue(rawValue, dependencies.formatDate)
    if (value) result.push({ label: dependencies.metricLabel(field), value })
  }

  const dimension = occurrence.details?.dimension ?? occurrence.dimension
  if (dimension && typeof dimension === 'object') {
    for (const [field, value] of Object.entries(dimension)) add(field, value)
  } else if (typeof dimension === 'string') {
    add('dimension', dimension)
  }
  if (result.length) return result

  const segmentName = occurrence.segment_name
  const subSegmentName = occurrence.details?.sub_segment_name ?? occurrence.sub_segment_name
  if (segmentName && subSegmentName) {
    const [field = '', segment = ''] = segmentName.split(' - ')
    if (segment === subSegmentName) {
      add(field || 'segment', subSegmentName)
      if (result.length) return result
    }
  }
  if (subSegmentName) add('segment', subSegmentName)
  return result
}
